import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Settings2, X, Cookie } from "lucide-react";
import { useCookieConsent } from "@/contexts/CookieConsentContext";

export default function CookieBanner() {
  const { bannerVisible, acceptAll, refuseAll, saveCustom, consent } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [localConsent, setLocalConsent] = useState({
    analytics: consent?.analytics ?? false,
    marketing: consent?.marketing ?? false,
  });

  if (!bannerVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
      <div
        className="max-w-5xl mx-auto rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #161616 0%, #0D0D0D 100%)",
          border: "1px solid rgba(201,168,76,0.28)",
          boxShadow: "0 24px 60px -16px rgba(0,0,0,0.8), 0 0 0 1px rgba(0,0,0,0.4)",
        }}
      >
        {/* Filet doré en haut */}
        <div className="h-px w-full" style={{background: 'linear-gradient(to right, transparent, rgba(201,168,76,0.7), transparent)'}} />

        <div className="p-5 md:p-7">
          {!showDetails ? (
            /* Vue principale */
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div className="flex items-start gap-3.5 flex-1">
                <div className="rounded-full p-2.5 mt-0.5 flex-shrink-0" style={{background:'rgba(201,168,76,0.12)', border:'1px solid rgba(201,168,76,0.3)'}}>
                  <Cookie className="w-5 h-5" style={{color:'var(--vea-or)'}} />
                </div>
                <div>
                  <p className="font-semibold mb-1" style={{color:'var(--vea-or)', fontFamily:"'Playfair Display', serif", fontSize:'1.05rem'}}>Respect de votre vie privée</p>
                  <p className="text-sm leading-relaxed" style={{color:'rgba(255,255,255,0.65)'}}>
                    Nous utilisons des cookies pour améliorer votre expérience et analyser notre trafic.
                    Vous pouvez choisir les cookies que vous acceptez.{" "}
                    <Link href="/politique-cookies" className="underline underline-offset-2" style={{color:'var(--vea-or)'}}>
                      En savoir plus
                    </Link>
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2.5 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDetails(true)}
                  className="rounded-full bg-transparent text-xs uppercase tracking-wide"
                  style={{borderColor:'rgba(255,255,255,0.18)', color:'rgba(255,255,255,0.8)'}}
                >
                  <Settings2 className="w-3.5 h-3.5 mr-1.5" />
                  Personnaliser
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refuseAll}
                  className="rounded-full bg-transparent text-xs uppercase tracking-wide"
                  style={{borderColor:'rgba(255,255,255,0.18)', color:'rgba(255,255,255,0.8)'}}
                >
                  <X className="w-3.5 h-3.5 mr-1.5" />
                  Tout refuser
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="rounded-full text-xs uppercase tracking-wide font-bold hover:brightness-110"
                  style={{background:'var(--vea-or)', color:'#0D0D0D', boxShadow:'0 6px 18px -6px rgba(201,168,76,0.5)'}}
                >
                  Tout accepter
                </Button>
              </div>
            </div>
          ) : (
            /* Vue détaillée */
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold" style={{fontFamily:"'Playfair Display', serif", color:'var(--vea-or)'}}>
                  Personnaliser mes préférences
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                  aria-label="Retour"
                >
                  <X className="w-4 h-4" style={{color:'rgba(255,255,255,0.6)'}} />
                </button>
              </div>

              <div className="space-y-3 mb-5">
                {/* Cookies essentiels - toujours activés */}
                <div className="flex items-center justify-between p-3.5 rounded-2xl" style={{background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)'}}>
                  <div>
                    <p className="font-medium text-sm" style={{color:'rgba(255,255,255,0.92)'}}>Cookies essentiels</p>
                    <p className="text-xs mt-0.5" style={{color:'rgba(255,255,255,0.45)'}}>Nécessaires au fonctionnement du site</p>
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{color:'var(--vea-or)'}}>
                    Toujours actifs
                  </span>
                </div>

                {/* Analytics */}
                <div className="flex items-center justify-between p-3.5 rounded-2xl" style={{background:'rgba(201,168,76,0.06)', border:'1px solid rgba(201,168,76,0.16)'}}>
                  <div>
                    <p className="font-medium text-sm" style={{color:'rgba(255,255,255,0.92)'}}>Cookies analytiques</p>
                    <p className="text-xs mt-0.5" style={{color:'rgba(255,255,255,0.45)'}}>Mesure d'audience anonymisée (Google Analytics)</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={localConsent.analytics}
                      onChange={e => setLocalConsent(prev => ({ ...prev, analytics: e.target.checked }))}
                    />
                    <div
                      className="w-10 h-6 rounded-full peer-checked:after:translate-x-4 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"
                      style={{
                        background: localConsent.analytics ? 'var(--vea-or)' : 'rgba(255,255,255,0.18)',
                        position: 'relative',
                      }}
                    />
                  </label>
                </div>

                {/* Marketing */}
                <div className="flex items-center justify-between p-3.5 rounded-2xl" style={{background:'rgba(201,168,76,0.06)', border:'1px solid rgba(201,168,76,0.16)'}}>
                  <div>
                    <p className="font-medium text-sm" style={{color:'rgba(255,255,255,0.92)'}}>Cookies marketing</p>
                    <p className="text-xs mt-0.5" style={{color:'rgba(255,255,255,0.45)'}}>Publicités personnalisées et réseaux sociaux</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={localConsent.marketing}
                      onChange={e => setLocalConsent(prev => ({ ...prev, marketing: e.target.checked }))}
                    />
                    <div
                      className="w-10 h-6 rounded-full peer-checked:after:translate-x-4 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"
                      style={{
                        background: localConsent.marketing ? 'var(--vea-or)' : 'rgba(255,255,255,0.18)',
                        position: 'relative',
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2.5 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refuseAll}
                  className="rounded-full bg-transparent text-xs uppercase tracking-wide"
                  style={{borderColor:'rgba(255,255,255,0.18)', color:'rgba(255,255,255,0.8)'}}
                >
                  Tout refuser
                </Button>
                <Button
                  size="sm"
                  onClick={() => saveCustom(localConsent)}
                  className="rounded-full text-xs uppercase tracking-wide font-semibold bg-transparent"
                  style={{border:'1px solid rgba(201,168,76,0.6)', color:'var(--vea-or)'}}
                >
                  Enregistrer mes choix
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="rounded-full text-xs uppercase tracking-wide font-bold hover:brightness-110"
                  style={{background:'var(--vea-or)', color:'#0D0D0D', boxShadow:'0 6px 18px -6px rgba(201,168,76,0.5)'}}
                >
                  Tout accepter
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
